import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useTerritoryStore } from '../../../store/territoryStore';
import type { Region, Territory } from '../../../types';

interface RegionTreeProps {
  onSelectRegion: (regionId: string) => void;
  onSelectTerritory: (territoryId: string) => void;
  onAddRegion: () => void;
  onAddTerritory: (regionId?: string) => void;
  selectedRegionId?: string;
  selectedTerritoryId?: string;
}

function TerritoryRow({
  territory,
  isSelected,
  onClick,
}: {
  territory: Territory;
  isSelected: boolean;
  onClick: () => void;
}) {
  const typeBadge =
    territory.territoryType === 'STATE'
      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
      : territory.territoryType === 'ZIP_CODE'
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
      : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300';

  const repName = territory.assignedUser?.displayName ?? 'Unassigned';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left pl-8 pr-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
        isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
    >
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge}`}
      >
        {territory.territoryType === 'ZIP_CODE' ? 'ZIP' : territory.territoryType}
      </span>
      <span className="text-sm text-gray-900 dark:text-gray-100 flex-1 truncate">{territory.name}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[100px]">{repName}</span>
    </button>
  );
}

function RegionRow({
  region,
  territories,
  isSelected,
  selectedTerritoryId,
  onSelectRegion,
  onSelectTerritory,
  onAddTerritory,
}: {
  region: Region;
  territories: Territory[];
  isSelected: boolean;
  selectedTerritoryId?: string;
  onSelectRegion: (id: string) => void;
  onSelectTerritory: (id: string) => void;
  onAddTerritory: (regionId: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const count = territories.length;

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
          isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
        }`}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <button
          onClick={() => onSelectRegion(region.id)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: region.color }}
          />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{region.name}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">({count})</span>
        </button>

        <button
          onClick={() => onAddTerritory(region.id)}
          className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 flex-shrink-0"
          title="Add territory to this region"
        >
          <Plus size={14} />
        </button>
      </div>

      {expanded && (
        <div>
          {territories.map((t) => (
            <TerritoryRow
              key={t.id}
              territory={t}
              isSelected={selectedTerritoryId === t.id}
              onClick={() => onSelectTerritory(t.id)}
            />
          ))}
          {count === 0 && (
            <p className="pl-8 pr-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 italic">No territories</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function RegionTree({
  onSelectRegion,
  onSelectTerritory,
  onAddRegion,
  onAddTerritory,
  selectedRegionId,
  selectedTerritoryId,
}: RegionTreeProps) {
  const { regions, territories, isLoadingRegions, fetchRegions, fetchTerritories } =
    useTerritoryStore();

  useEffect(() => {
    fetchRegions();
    fetchTerritories();
  }, [fetchRegions, fetchTerritories]);

  const territoriesByRegion = (regionId: string) =>
    territories.filter((t) => t.regionId === regionId && t.isActive);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Regions & Territories</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onAddTerritory()}
            className="text-xs border border-gray-300 dark:border-gray-600 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
          >
            + Territory
          </button>
          <button
            onClick={onAddRegion}
            className="text-xs bg-blue-600 text-white px-2 py-1 rounded-md hover:bg-blue-700"
          >
            + Region
          </button>
        </div>
      </div>

      <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
        {isLoadingRegions ? (
          <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        ) : regions.length === 0 ? (
          <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No regions yet. Create one to get started.</p>
        ) : (
          regions
            .filter((r) => r.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((region) => (
              <RegionRow
                key={region.id}
                region={region}
                territories={territoriesByRegion(region.id)}
                isSelected={selectedRegionId === region.id}
                selectedTerritoryId={selectedTerritoryId}
                onSelectRegion={onSelectRegion}
                onSelectTerritory={onSelectTerritory}
                onAddTerritory={onAddTerritory}
              />
            ))
        )}
      </div>
    </div>
  );
}
